import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    IconButton,
    Divider,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";

/* ---------- Design tokens (inline, no external CSS) ---------- */
const C = {
    primary: "#2563EB",
    primaryDark: "#1D4ED8",
    primarySoft: "EFF6FF",
    border: "#E5E7EB",
    text: "#111827",
    textMuted: "#6B7280",
    bg: "#F9FAFB",
    white: "#FFFFFF",
    codeBg: "#0F172A",
    codeText: "#E2E8F0",
    amber: "#B45309",
    amberSoft: "#FEF3C7",
    blue: "#1D4ED8",
    blueSoft: "#DBEAFE",
};

const fontStack = "var(--app-font)";

/* ---------- Small helpers ---------- */
const SectionLabel = ({ icon, title, tone = "primary" }) => {
    const toneMap = {
        primary: { bg: C.primarySoft, fg: C.primary },
        amber: { bg: C.amberSoft, fg: C.amber },
        blue: { bg: C.blueSoft, fg: C.blue },
    };
    const t = toneMap[tone];
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    bgcolor: t.bg,
                    color: t.fg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {icon}
            </Box>
            <Typography
                sx={{
                    fontFamily: fontStack,
                    fontSize: 14,
                    fontWeight: 600,
                    color: C.text,
                    letterSpacing: 0.2,
                }}
            >
                {title}
            </Typography>
        </Box>
    );
};

const CodeBlock = ({ value }) => (
    <Box
        component="pre"
        sx={{
            m: 0,
            p: 2,
            bgcolor: C.codeBg,
            color: C.codeText,
            borderRadius: "10px",
            fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
            fontSize: 12.5,
            lineHeight: 1.6,
            maxHeight: 320,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        }}
    >
        {JSON.stringify(value, null, 2)}
    </Box>
);

/* ---------- Component ---------- */
const ViewLogModal = ({ open, onClose, data = {} }) => {
    let parsed = {};
    try {
        parsed = JSON.parse(data?.action_data || "{}");
    } catch {
        parsed = {};
    }

    const { message, oldValues, newValues } = parsed;
    const hasOldData = oldValues && Object.keys(oldValues).length > 0;
    const hasNewData = newValues && Object.keys(newValues).length > 0;

    const actionType = data?.action_type || data?.action || null;
    const performedBy = data?.performed_by || data?.user_name || null;
    const createdAt = data?.created_at || data?.timestamp || null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                    fontFamily: fontStack,
                    boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    p: 0,
                    background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                    color: C.white,
                }}
            >
                <Box
                    sx={{
                        px: 3,
                        py: 2.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                bgcolor: "rgba(255,255,255,0.15)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
                                Activity Log Details
                            </Typography>
                            <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
                                Full record of the selected action
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{ color: C.white, "&:hover": { bgcolor: "rgba(255,255,255,0.12)" } }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Body */}
            <DialogContent sx={{ p: 0, bgcolor: C.bg }}>
                <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {/* Meta chips */}
                    {(actionType || performedBy || createdAt) && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {actionType && (
                                <Chip
                                    label={actionType}
                                    size="small"
                                    sx={{
                                        bgcolor: C.primarySoft,
                                        color: C.primary,
                                        fontWeight: 600,
                                        fontFamily: fontStack,
                                    }}
                                />
                            )}
                            {performedBy && (
                                <Chip
                                    label={`By: ${performedBy}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderColor: C.border, color: C.text, fontFamily: fontStack }}
                                />
                            )}
                            {createdAt && (
                                <Chip
                                    label={new Date(createdAt).toLocaleString()}
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderColor: C.border, color: C.textMuted, fontFamily: fontStack }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Message card */}
                    <Box
                        sx={{
                            bgcolor: C.white,
                            border: `1px solid ${C.border}`,
                            borderRadius: "12px",
                            p: 2.25,
                        }}
                    >
                        <SectionLabel
                            icon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
                            title="Message"
                        />
                        <Typography
                            sx={{
                                mt: 1.25,
                                fontSize: 14,
                                color: C.text,
                                lineHeight: 1.6,
                                fontFamily: fontStack,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                            }}
                        >
                            {message || "—"}
                        </Typography>
                    </Box>

                    {/* Old Data */}
                    {hasOldData && (
                        <Accordion
                            disableGutters
                            elevation={0}
                            defaultExpanded
                            sx={{
                                border: `1px solid ${C.border}`,
                                borderRadius: "12px !important",
                                overflow: "hidden",
                                "&:before": { display: "none" },
                                bgcolor: C.white,
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: C.textMuted }} />}
                                sx={{ px: 2.25, py: 1.25 }}
                            >
                                <SectionLabel
                                    icon={<HistoryOutlinedIcon sx={{ fontSize: 16 }} />}
                                    title="Old Data"
                                    tone="amber"
                                />
                            </AccordionSummary>
                            <Divider sx={{ borderColor: C.border }} />
                            <AccordionDetails sx={{ p: 2 }}>
                                <CodeBlock value={oldValues} />
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* New Data */}
                    {hasNewData && (
                        <Accordion
                            disableGutters
                            elevation={0}
                            defaultExpanded
                            sx={{
                                border: `1px solid ${C.border}`,
                                borderRadius: "12px !important",
                                overflow: "hidden",
                                "&:before": { display: "none" },
                                bgcolor: C.white,
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: C.textMuted }} />}
                                sx={{ px: 2.25, py: 1.25 }}
                            >
                                <SectionLabel
                                    icon={<FiberNewOutlinedIcon sx={{ fontSize: 16 }} />}
                                    title="New Data"
                                    tone="blue"
                                />
                            </AccordionSummary>
                            <Divider sx={{ borderColor: C.border }} />
                            <AccordionDetails sx={{ p: 2 }}>
                                <CodeBlock value={newValues} />
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Empty state */}
                    {!hasOldData && !hasNewData && (
                        <Box
                            sx={{
                                bgcolor: C.white,
                                border: `1px dashed ${C.border}`,
                                borderRadius: "12px",
                                py: 4,
                                textAlign: "center",
                            }}
                        >
                            <Typography sx={{ fontSize: 14, color: C.textMuted, fontFamily: fontStack }}>
                                No additional data available for this log entry.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Footer */}
            <Divider sx={{ borderColor: C.border }} />
            <DialogActions sx={{ px: 3, py: 2, bgcolor: C.white }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    disableElevation
                    sx={{
                        textTransform: "none",
                        fontFamily: fontStack,
                        fontWeight: 600,
                        fontSize: 14,
                        px: 3,
                        py: 1,
                        borderRadius: "10px",
                        bgcolor: C.primary,
                        "&:hover": { bgcolor: C.primaryDark },
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewLogModal;
